import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Switch,
  Text,
  useClipboard,
  useToast,
} from "@chakra-ui/react";
import { Control, Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FiCheck, FiCopy, FiRepeat } from "react-icons/fi";
import { AdminOrgDto, useAdminUpdateOrganizationMutation } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useApiError } from "../../../common/errors/useApiError";
import { DebugPrint } from "../DebugPrint";
import { getOrgAccessValues, OrgAccessFlagKey, OrgAccessGroup, useOrgAccessGroups } from "./useOrgAccessGroups";

interface Props {
  organization: AdminOrgDto;
  onClose: () => void;
  onImpersonate: () => void;
}

type FormValues = { name: string } & Record<OrgAccessFlagKey, boolean>;

export const AdminEditOrganizationDrawer = ({ organization, onClose, onImpersonate }: Props) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const toast = useToast();
  const apiErrorDisplayer = useApiError();
  const groups = useOrgAccessGroups();
  const { onCopy, hasCopied } = useClipboard(organization.id);
  const [update, { isLoading }] = useAdminUpdateOrganizationMutation();

  const {
    handleSubmit,
    register,
    reset,
    watch,
    control,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    defaultValues: { name: organization.name, ...getOrgAccessValues(organization) },
  });

  const values = watch();
  const dirtyCount = Object.keys(dirtyFields).length;

  const submit = async (formValues: FormValues) => {
    try {
      await update({
        organizationId: authState.selectedOrg.id,
        orgId: organization.id,
        adminUpdateOrganizationRequest: formValues,
      }).unwrap();
      toast({ title: t("Organization updated!"), status: "success", position: "top-right" });
      onClose();
    } catch (e) {
      apiErrorDisplayer.trigger(e);
    }
  };

  return (
    <Drawer isOpen={true} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottom="1px solid" borderColor="smBorder" pr="12">
          <Flex justify="space-between" align="flex-start" gap="3">
            <Box minW="0">
              <Text noOfLines={1}>{organization.name}</Text>
              <HStack spacing="1" mt="0.5">
                <Text fontSize="xs" fontWeight="normal" color="smMuted" noOfLines={1}>
                  {organization.id}
                </Text>
                <IconButton
                  aria-label={t("Copy")}
                  icon={hasCopied ? <FiCheck /> : <FiCopy />}
                  size="xs"
                  variant="ghost"
                  onClick={onCopy}
                />
              </HStack>
              <Text fontSize="xs" fontWeight="normal" color="smMuted" noOfLines={1}>
                {`${organization.owner.firstName ?? ""} ${organization.owner.lastName ?? ""}`.trim()} ·{" "}
                {organization.owner.email} · {organization.country.name}
              </Text>
            </Box>
            <Button
              size="sm"
              variant="outline"
              leftIcon={<FiRepeat />}
              flexShrink={0}
              onClick={onImpersonate}
            >
              {t("Impersonate")}
            </Button>
          </Flex>
        </DrawerHeader>
        <DrawerBody as="form" id="admin-edit-org-form" onSubmit={handleSubmit(submit)}>
          <FormControl isInvalid={errors.name !== undefined} pt="2">
            <FormLabel htmlFor="name" fontSize="sm">
              {t("Organization name")}
            </FormLabel>
            <Input
              id="name"
              size="sm"
              rounded="md"
              {...register("name", {
                required: t("Please provide a name for the organization") ?? "",
                minLength: { value: 2, message: t("Must be at least 2 characters long") },
              })}
            />
            <FormErrorMessage>{errors.name && errors.name.message}</FormErrorMessage>
          </FormControl>
          {groups.map((group) => (
            <AccessGroup key={group.title} group={group} values={values} control={control} />
          ))}
          <Accordion allowToggle mt="6" mb="4">
            <AccordionItem border="1px dashed" borderColor="smBorder" rounded="md">
              <AccordionButton fontSize="sm" color="smMuted">
                <Box as="span" flex="1" textAlign="left">
                  {t("Raw JSON (debug)")}
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel px="2" pb="2">
                <DebugPrint data={organization} />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </DrawerBody>
        <DrawerFooter borderTop="1px solid" borderColor="smBorder" justifyContent="flex-start" gap="4">
          <HStack>
            <Button
              size="sm"
              type="submit"
              form="admin-edit-org-form"
              colorScheme="teal"
              isLoading={isLoading}
              isDisabled={dirtyCount === 0}
            >
              {t("Save")}
            </Button>
            <Button size="sm" variant="ghost" isDisabled={dirtyCount === 0} onClick={() => reset()}>
              {t("Discard")}
            </Button>
          </HStack>
          <Text fontSize="sm" color="smMuted">
            {dirtyCount > 0 ? `${t("Unsaved changes")}: ${dirtyCount}` : t("No unsaved changes")}
          </Text>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const AccessGroup = ({
  group,
  values,
  control,
}: {
  group: OrgAccessGroup;
  values: FormValues;
  control: Control<FormValues>;
}) => {
  const { t } = useTranslation();
  const enabledCount = group.flags.filter((flag) => values[flag.key]).length;
  return (
    <Box mt="6">
      <Flex justify="space-between" align="baseline" mb="1">
        <Text fontSize="xs" fontWeight="bold" textTransform="uppercase" letterSpacing="wide" color="smMuted">
          {group.title}
        </Text>
        <Text fontSize="xs" color="smTextDisabled">
          {enabledCount}/{group.flags.length} {t("enabled")}
        </Text>
      </Flex>
      {group.flags.map((flag) => (
        <Flex
          key={flag.key}
          justify="space-between"
          align="center"
          py="1.5"
          borderBottom="1px solid"
          borderColor="smBorder"
          _last={{ borderBottom: "none" }}
        >
          <FormLabel htmlFor={flag.key} fontSize="sm" fontWeight="normal" m="0" flex="1" cursor="pointer">
            {flag.label}
          </FormLabel>
          <Controller
            name={flag.key}
            control={control}
            render={({ field }) => (
              <Switch
                id={flag.key}
                size="sm"
                colorScheme="teal"
                isChecked={field.value}
                onChange={(e) => field.onChange(e.target.checked)}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            )}
          />
        </Flex>
      ))}
    </Box>
  );
};
